/* eslint-disable no-console */
import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import fetchJSON from "common/utils/fetchJSON";
import { Park } from "types/Park";
import overrideTranslations from "common/utils/overrideTranslation";

type AuthPark = {
  park?: Park;
  refreshPark: (parkId: any) => Promise<Park>;
  emptyPark: () => void;
  isFetching: boolean;
};

export const ParkContext = createContext<AuthPark>({} as AuthPark);

type Props = {
  children: React.ReactNode;
};

export function ParkProvider({ children }: Props) {
  const [park, setPark] = useState<Park>();
  const [isFetching, setIsFetching] = useState(false);

  const refreshPark = useCallback(
    async (parkId: any) => {
      try {
        setIsFetching(true);

        if (parkId) {
          const res = await fetchJSON({
            url: `parks/${parkId}`,
            method: "GET",
          });
          if (res) {
            setPark(
              res
                ? {
                    ...res,
                  }
                : undefined
            );
            if (res?.customTranslations) {
              overrideTranslations(res?.customTranslations);
            }

            return res;
          }
        } else {
          setPark(undefined);
        }

        return false;
      } catch (e) {
        console.log(e);
        // if users/me is not accessible then the token expired
        return false;
      } finally {
        setIsFetching(false);
      }
    },
    [setPark]
  ); // pushToken

  const emptyPark = useCallback(() => {
    setPark(undefined);
  }, [setPark]);

  // Apply translations whenever park.customTranslations changes
  useEffect(() => {
    if (park?.customTranslations) {
      overrideTranslations(park?.customTranslations);
    }
  }, [park?.customTranslations, park?.id]);

  const value: AuthPark = useMemo(
    () => ({
      park,
      setPark,
      refreshPark,
      isFetching,
      emptyPark,
    }),
    [park, setPark, refreshPark, isFetching, emptyPark]
  );

  return <ParkContext.Provider value={value}>{children}</ParkContext.Provider>;
}
